import React from 'react'

import { Box, SimpleGrid, Stack, Tooltip } from '@chakra-ui/react'
import {
  Button,
  DataGrid,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tag,
  TColumnDef,
  TTagVariant,
  Typography,
} from '@pnld/components-web'

import { useQueryGetProcessingDetail } from '@/api/prediction-processing/queries'
import {
  TCsvReport,
  TCsvReportTable,
  TProcessingDetail,
} from '@/api/prediction-processing/types'

import { STATUS_SHORT_LABEL, STATUS_VARIANT_MAP } from './constants'
import {
  CENSUS_TABLES,
  formatCount,
  formatDateTime,
  StatusAlert,
  tablePosition,
  TAlertStatus,
} from './shared'

interface IProcessingDetailModalProps {
  processingId: number | null
  onClose: () => void
}

type TImportRow = {
  label: string
  rows: number
  missingColumns: Array<string>
  isTotal?: boolean
}

const REPORT_LABELS: Record<string, string> = Object.fromEntries(
  CENSUS_TABLES.map(({ model, label }) => [model, label])
)

// Enquanto o arquivo sobe ou é importado, ainda não há relatório.
const IMPORT_PENDING = ['CSV_UPLOADING', 'CSV_PROCESSING']

const ALERT_BY_VARIANT: Record<string, TAlertStatus> = {
  negative: 'error',
  positive: 'success',
  primary: 'info',
  neutral: 'warning',
}

const plural = (count: number, singular: string, pluralForm: string) =>
  `${formatCount(count)} ${count === 1 ? singular : pluralForm}`

const importRows = (report: TCsvReport): Array<TImportRow> => {
  const rows = Object.entries(report)
    .filter(([model]) => model !== 'totalData')
    .sort(([a], [b]) => tablePosition(a) - tablePosition(b))
    .map(([model, entry]) => {
      const table = entry as TCsvReportTable
      return {
        label: REPORT_LABELS[model] ?? model,
        rows: table.nuAddedData,
        missingColumns: table.ndsMissingColumns ?? [],
      }
    })
  const total = rows.reduce((sum, row) => sum + row.rows, 0)
  return [
    ...rows,
    { label: 'Total', rows: total, missingColumns: [], isTotal: true },
  ]
}

const cellText = (row: TImportRow, text: string) => (
  <Typography
    variant={row.isTotal ? 'h-2xs' : 'b-sm'}
    color="brand.neutral.dark_2"
  >
    {text}
  </Typography>
)

const MissingColumns = ({ row }: { row: TImportRow }) => {
  if (row.isTotal) return cellText(row, '—')
  if (row.missingColumns.length === 0) return cellText(row, 'nenhuma')
  return (
    <Tooltip label={row.missingColumns.join(', ')} placement="top">
      <Box display="inline-block" cursor="help">
        <Typography
          variant="b-sm"
          color="brand.neutral.dark_2"
          textDecoration="underline dotted"
        >
          {plural(row.missingColumns.length, 'coluna', 'colunas')}
        </Typography>
      </Box>
    </Tooltip>
  )
}

const importColumns: Array<TColumnDef<TImportRow>> = [
  {
    accessorKey: 'label',
    header: 'Tabela',
    cell: ({ row }) => cellText(row.original, row.original.label),
  },
  {
    accessorKey: 'rows',
    header: 'Linhas importadas',
    cell: ({ row }) => cellText(row.original, formatCount(row.original.rows)),
  },
  {
    id: 'missingColumns',
    header: 'Colunas ausentes',
    cell: ({ row }) => <MissingColumns row={row.original} />,
  },
]

const Field = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <Stack gap={0}>
    <Typography variant="b-sm" color="brand.neutral.dark_1">
      {label}
    </Typography>
    <Typography variant="s-sm" color="brand.neutral.dark_2">
      {children}
    </Typography>
  </Stack>
)

const Section = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <Stack gap={2}>
    <Typography variant="h-2xs" color="brand.neutral.dark_2">
      {title}
    </Typography>
    {children}
  </Stack>
)

const triggerText = (detail: TProcessingDetail) => {
  if (detail.nuTriggerAttempts === 0) return '—'
  return `${plural(
    detail.nuTriggerAttempts,
    'tentativa',
    'tentativas'
  )}, a última em ${formatDateTime(detail.dtLastTriggerAttempt)}`
}

const ImportPanel = ({ detail }: { detail: TProcessingDetail }) => {
  const report = detail.dsCsvReport
  if (!report) {
    return (
      <Typography variant="b-sm">
        {IMPORT_PENDING.includes(detail.dsStatus)
          ? 'O import ainda não terminou.'
          : 'Este envio não importou dados.'}
      </Typography>
    )
  }
  const total = report.totalData
  return (
    <Stack gap={2}>
      {total && (
        <Typography variant="b-sm">
          Censo {total.nuCensusYear}:{' '}
          {plural(total.nuTotalData, 'escola', 'escolas')} e{' '}
          {plural(total.nuTotalColumns, 'coluna', 'colunas')} no arquivo.
        </Typography>
      )}
      <DataGrid
        columns={importColumns}
        data={importRows(report)}
        hasPagination={false}
        // Ordenar tiraria a linha de total do fim.
        enableSorting={false}
      />
      <Typography variant="b-sm" color="brand.neutral.dark_1">
        Colunas ausentes: colunas que o sistema espera e o arquivo não trouxe;
        ficam vazias no banco.
      </Typography>
    </Stack>
  )
}

const SummaryPanel = ({
  detail,
  variant,
}: {
  detail: TProcessingDetail
  variant: TTagVariant
}) => {
  const confirmedYear = detail.nuConfirmedCensusYear
  return (
    <Stack gap={5}>
      <Section title="Envio">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={3}>
          <Field label="Arquivo">{detail.dsOriginalFilename ?? '—'}</Field>
          <Field label="Enviado por">
            {detail.coUser?.noUserName || detail.coUser?.dsEmail || '—'}
          </Field>
          <Field label="Ano do Censo no arquivo">
            {detail.nuCensusYear ?? '—'}
          </Field>
          <Field label="Ano confirmado no envio">{confirmedYear ?? '—'}</Field>
          <Field label="Substituição confirmada">
            {confirmedYear === null
              ? '—'
              : detail.stReplaceConfirmed
              ? 'Sim'
              : 'Não'}
          </Field>
          <Field label="Ano de predição">
            {detail.nuPredictionTargetYear ?? '—'}
          </Field>
        </SimpleGrid>
        <Stack gap={1} align="flex-start">
          <Typography variant="b-sm" color="brand.neutral.dark_1">
            Status
          </Typography>
          <Tag variant={variant}>
            {STATUS_SHORT_LABEL[detail.dsStatus] ?? detail.dsStatusDisplay}
          </Tag>
          <Typography variant="b-sm">{detail.dsStatusDisplay}</Typography>
        </Stack>
      </Section>

      {/* Fim de cada fase, com sucesso ou falha: o desfecho está no status. */}
      <Section title="Etapas">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={3}>
          <Field label="Arquivo enviado em">
            {formatDateTime(detail.dtCreatedAt)}
          </Field>
          <Field label="Import encerrado em">
            {formatDateTime(detail.dtCsvFinishedAt)}
          </Field>
          <Field label="Disparos da predição">{triggerText(detail)}</Field>
          <Field label="Job da predição">
            {detail.coPredictionJobId ?? '—'}
          </Field>
          <Field label="Predição encerrada em">
            {formatDateTime(detail.dtPredictionFinishedAt)}
          </Field>
        </SimpleGrid>
      </Section>
    </Stack>
  )
}

// A mensagem fica acima das abas: é o desfecho do envio.
const DetailContent = ({ detail }: { detail: TProcessingDetail }) => {
  const variant = STATUS_VARIANT_MAP[detail.dsStatus] ?? 'neutral'
  return (
    <Stack gap={4}>
      {detail.dsResultMessage && (
        <StatusAlert status={ALERT_BY_VARIANT[variant] ?? 'warning'}>
          {detail.dsResultMessage}
        </StatusAlert>
      )}
      <Tab
        panels={[
          {
            title: 'Resumo',
            render: () => <SummaryPanel detail={detail} variant={variant} />,
          },
          {
            title: 'Dados importados',
            render: () => <ImportPanel detail={detail} />,
          },
        ]}
      />
    </Stack>
  )
}

const ProcessingDetailModal = ({
  processingId,
  onClose,
}: IProcessingDetailModalProps) => {
  const { data, isLoading, isError } = useQueryGetProcessingDetail(processingId)

  return (
    <Modal
      isOpen={processingId !== null}
      onClose={onClose}
      // Título e Fechar ficam fixos; só o conteúdo rola.
      scrollBehavior="inside"
    >
      <ModalContent maxW="800px">
        <ModalHeader>
          <Typography variant="h-xs" color="brand.neutral.dark_2">
            Detalhes do envio
          </Typography>
        </ModalHeader>
        <ModalBody>
          {isError ? (
            <StatusAlert status="error">
              Não foi possível carregar os detalhes do envio. Feche e abra de
              novo.
            </StatusAlert>
          ) : isLoading || !data ? (
            <Typography variant="b-sm">
              Carregando os detalhes do envio…
            </Typography>
          ) : (
            <DetailContent detail={data} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button label="Fechar" variant="secondary" onClick={onClose} />
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ProcessingDetailModal
